import { ModuleShell } from '../components/ModuleShell';
import { Mono, StatusChip, EmptyState } from '../components/StatusChip';
import type { InboxResponse, ModuleState } from '../api/types';
import type { Filters } from '../state/useURLState';

interface Props { moduleState: ModuleState<InboxResponse> & { refetch: () => void }; filters: Filters }

export function InboxModule({ moduleState, filters }: Props) {
  const data = moduleState.data;
  const shadow = data?.shadow_count;
  const stale = data?.stale_conversations || [];

  // Filter by_client if client filter active
  const byClient = shadow?.by_client || {};
  const filteredClients = filters.client
    ? Object.entries(byClient).filter(([k]) => k === filters.client)
    : Object.entries(byClient);

  const totalCanonical = shadow?.canonical_total ?? 0;
  const totalUnresolved = shadow?.unresolved_total ?? 0;

  return (
    <ModuleShell name="inbox" title="Stale Interested / Inbox" status={moduleState.status} error={moduleState.error} dataFreshness={data?.data_as_of ?? moduleState.dataFreshness} fetchedAt={moduleState.fetchedAt} isStale={moduleState.isStale} truth={moduleState.truth} staleReason={moduleState.staleReason} onRetry={moduleState.refetch}
      headerRight={<div style={{ display: 'flex', gap: 8 }}>
        <StatusChip label={`${shadow?.total_interested_conversations ?? 0} conversations`} variant="info" />
        {totalUnresolved > 0 && <StatusChip label={`${totalUnresolved} unresolved`} variant="warning" />}
      </div>}>
      
      {/* Truncation warning */}
      {shadow?.conversations_truncated && (
        <div style={{ padding: '8px 16px', marginBottom: 12, background: 'var(--yellow-bg)', border: '1px solid var(--yellow-border)', borderRadius: 'var(--radius-md)', fontSize: 11, color: 'var(--yellow)' }}>
          ⚠ Conversation list was truncated — totals may be incomplete
        </div>
      )}

      {shadow?.warning && (
        <div style={{ padding: '8px 16px', marginBottom: 12, background: 'var(--orange-bg)', border: '1px solid var(--orange-border)', borderRadius: 'var(--radius-md)', fontSize: 11, color: 'var(--orange)' }}>
          {shadow.warning}
        </div>
      )}

      {/* Shadow truth summary */}
      {shadow && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, padding: '16px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)', marginBottom: 16 }}>
          {[
            { l: 'CANONICAL', v: totalCanonical, c: 'var(--text-primary)' },
            { l: 'UNRESOLVED', v: totalUnresolved, c: totalUnresolved > 0 ? 'var(--yellow)' : 'var(--green)' },
            { l: 'TOTAL INTERESTED', v: shadow.total_interested_conversations, c: 'var(--blue-bright)' },
            { l: 'DATE', v: shadow.date, c: 'var(--text-muted)' },
          ].map(x => (
            <div key={x.l} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: 'var(--text-ghost)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 2 }}>{x.l}</div>
              <Mono color={x.c} style={{ fontSize: 18, fontWeight: 700 }}>{typeof x.v === 'number' ? x.v.toLocaleString() : x.v}</Mono>
            </div>
          ))}
        </div>
      )}

      {/* Per-client breakdown */}
      <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Interested by Client</h3>
      {filteredClients.length === 0 ? (
        <EmptyState title="No client data" sub="Shadow count has no per-client breakdown for this filter." />
      ) : (
        <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          {filteredClients.map(([clientId, count]) => (
            <div key={clientId} data-hoverable="" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid var(--border-faint)', transition: 'background var(--ease-fast)' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{clientId}</span>
              <Mono color={count > 0 ? 'var(--blue-bright)' : 'var(--text-dim)'}>{count}</Mono>
            </div>
          ))}
        </div>
      )}

      {/* Unresolved breakdown */}
      {totalUnresolved > 0 && shadow?.unresolved_by_guess && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
            Unresolved <StatusChip label="shadow" variant="proxy" />
          </h3>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
            {Object.entries(shadow.unresolved_by_guess).map(([guess, count]) => (
              <div key={guess} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid var(--border-faint)' }}>
                <span style={{ fontSize: 12, color: guess === 'unknown' ? 'var(--text-ghost)' : 'var(--text-secondary)', fontWeight: 600 }}>
                  {guess === 'unknown' ? '(unattributed)' : guess}
                </span>
                <Mono color="var(--yellow)">{count}</Mono>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stale conversations */}
      {stale.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Stale Conversations</h3>
          <pre style={{ padding: 16, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', overflow: 'auto', maxHeight: 300 }}>
            {JSON.stringify(stale, null, 2)}
          </pre>
        </div>
      )}
    </ModuleShell>
  );
}
